import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileDown, Search, Shield } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface Template {
  id: string;
  title: string;
  description: string;
  category: TemplateCategory;
  /** Public URL — government/Sebrae/OAB official sources. */
  url: string;
  /** Display label for the source so the user knows where it comes from. */
  source: string;
  /** Free-text tags to help search. */
  tags?: string[];
}

type TemplateCategory =
  | 'comercial'
  | 'societario'
  | 'imobiliario'
  | 'trabalho'
  | 'familia'
  | 'consumidor'
  | 'mei_simples'
  | 'tecnologia'
  | 'civil';

const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  comercial: 'Comercial',
  societario: 'Societário',
  imobiliario: 'Imobiliário',
  trabalho: 'Trabalho',
  familia: 'Família',
  consumidor: 'Consumidor',
  mei_simples: 'MEI / Simples',
  tecnologia: 'Tecnologia',
  civil: 'Civil',
};

/**
 * Curated templates linking to OFFICIAL public sources (Sebrae, Receita,
 * Gov.br, OAB). We never host the documents — only point to the canonical
 * source so users always get the latest version. If the lawyer wants
 * something more custom, they contract via the platform.
 */
const TEMPLATES: Template[] = [
  {
    id: 'nda-sebrae',
    title: 'Termo de Confidencialidade (NDA)',
    description: 'Modelo básico de NDA bilateral para troca de informações estratégicas entre empresas.',
    category: 'comercial',
    url: 'https://www.sebrae.com.br/sites/PortalSebrae/artigos/protecao-de-informacoes-empresariais-com-nda,c8aab8ce4f7ed510VgnVCM1000004c00210aRCRD',
    source: 'Sebrae',
    tags: ['NDA', 'confidencialidade', 'startup'],
  },
  {
    id: 'prestacao-servicos-sebrae',
    title: 'Contrato de Prestação de Serviços',
    description: 'Modelo genérico de prestação de serviços PJ → PJ, customizável.',
    category: 'comercial',
    url: 'https://www.sebrae.com.br/sites/PortalSebrae/artigos/contratos-empresariais',
    source: 'Sebrae',
    tags: ['servicos', 'PJ'],
  },
  {
    id: 'compra-venda-sebrae',
    title: 'Contrato de Compra e Venda',
    description: 'Compra e venda de bens móveis entre empresas ou pessoas físicas.',
    category: 'comercial',
    url: 'https://www.sebrae.com.br/sites/PortalSebrae/artigos',
    source: 'Sebrae',
    tags: ['compra', 'venda'],
  },
  {
    id: 'contrato-social-mei',
    title: 'CCMEI — Certificado MEI',
    description: 'Emissão e download do Certificado de Condição de Microempreendedor Individual.',
    category: 'mei_simples',
    url: 'https://www.gov.br/empresas-e-negocios/pt-br/empreendedor/servicos-para-mei/emissao-de-comprovante-ccmei',
    source: 'Gov.br',
    tags: ['MEI', 'CCMEI'],
  },
  {
    id: 'simples-nacional-opcao',
    title: 'Opção pelo Simples Nacional',
    description: 'Termo de opção e modelos para enquadramento no regime do Simples Nacional.',
    category: 'mei_simples',
    url: 'https://www8.receita.fazenda.gov.br/SimplesNacional/',
    source: 'Receita Federal',
    tags: ['Simples Nacional', 'tributário'],
  },
  {
    id: 'procuracao-pf',
    title: 'Procuração ad judicia (modelo)',
    description: 'Modelo básico de procuração para representação judicial — adaptar ao caso concreto.',
    category: 'civil',
    url: 'https://www.gov.br/pt-br/servicos',
    source: 'Gov.br',
    tags: ['procuração', 'judicial'],
  },
  {
    id: 'locacao-residencial',
    title: 'Contrato de Locação Residencial',
    description: 'Modelo de locação residencial conforme Lei 8.245/91 (Lei do Inquilinato).',
    category: 'imobiliario',
    url: 'https://www.planalto.gov.br/ccivil_03/leis/l8245.htm',
    source: 'Planalto (base legal)',
    tags: ['locação', 'aluguel', 'inquilinato'],
  },
  {
    id: 'rescisao-trabalho',
    title: 'Termo de Rescisão Contratual (CLT)',
    description: 'Modelo de TRCT — Termo de Rescisão do Contrato de Trabalho usado na rescisão.',
    category: 'trabalho',
    url: 'https://www.gov.br/trabalho-e-emprego/pt-br/assuntos/contratos-trabalho',
    source: 'Gov.br',
    tags: ['CLT', 'rescisão', 'TRCT'],
  },
  {
    id: 'divorcio-extrajudicial',
    title: 'Divórcio Consensual Extrajudicial',
    description: 'Roteiro do divórcio extrajudicial em cartório (Resolução CNJ 35/2007).',
    category: 'familia',
    url: 'https://www.cnj.jus.br/poder-judiciario/atos-normativos/',
    source: 'CNJ',
    tags: ['divórcio', 'extrajudicial', 'cartório'],
  },
  {
    id: 'reclamacao-consumidor',
    title: 'Reclamação no Consumidor.gov.br',
    description: 'Plataforma oficial para reclamações de consumidor contra empresas cadastradas.',
    category: 'consumidor',
    url: 'https://www.consumidor.gov.br/',
    source: 'Senacon',
    tags: ['consumidor', 'reclamação'],
  },
  {
    id: 'termos-uso-tech',
    title: 'Modelo de Termos de Uso (SaaS)',
    description: 'Referência para termos de uso de plataformas SaaS — adaptar conforme LGPD.',
    category: 'tecnologia',
    url: 'https://www.gov.br/anpd/pt-br',
    source: 'ANPD (LGPD)',
    tags: ['SaaS', 'termos de uso', 'LGPD'],
  },
  {
    id: 'politica-privacidade',
    title: 'Política de Privacidade (LGPD)',
    description: 'Guia oficial da ANPD para elaboração de política de privacidade conforme LGPD.',
    category: 'tecnologia',
    url: 'https://www.gov.br/anpd/pt-br/assuntos/noticias',
    source: 'ANPD',
    tags: ['LGPD', 'privacidade'],
  },
];

const CATEGORIES: TemplateCategory[] = [
  'comercial',
  'societario',
  'imobiliario',
  'trabalho',
  'familia',
  'consumidor',
  'mei_simples',
  'tecnologia',
  'civil',
];

export function Templates() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<TemplateCategory | 'all'>('all');

  const normalized = useMemo(
    () =>
      search
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, ''),
    [search],
  );

  const filtered = useMemo(() => {
    let arr = TEMPLATES;
    if (category !== 'all') arr = arr.filter((t) => t.category === category);
    if (normalized) {
      arr = arr.filter((t) => {
        const hay = `${t.title} ${t.description} ${(t.tags ?? []).join(' ')} ${t.source}`
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '');
        return hay.includes(normalized);
      });
    }
    return arr;
  }, [category, normalized]);

  return (
    <MarketingLayout>
      <div className="container-px mx-auto max-w-5xl py-16">
        <Badge variant="accent" className="mb-2">Templates grátis</Badge>
        <h1 className="font-serif text-display-lg">Biblioteca jurídica</h1>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          Modelos e formulários jurídicos baseados em fontes oficiais brasileiras (Sebrae, Receita,
          Gov.br, CNJ, ANPD). Use como ponto de partida — pra algo customizado pro seu caso,{' '}
          <Link to="/advogados" className="text-accent hover:underline">
            contrate um advogado parceiro
          </Link>
          .
        </p>

        <div className="mt-6 rounded-md border border-warning/30 bg-warning/[0.04] p-3 text-sm flex items-start gap-2">
          <Shield className="h-4 w-4 text-warning mt-0.5 flex-none" />
          <p className="text-muted-foreground">
            <strong className="text-foreground">Templates são pontos de partida</strong>, não pareceres
            jurídicos. Adapte ao seu caso concreto e, em situações de risco, valide com advogado.
          </p>
        </div>

        {/* Search */}
        <div className="relative mt-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Buscar (ex: NDA, locação, MEI, divórcio…)"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Category chips */}
        <div className="mt-4 flex flex-wrap gap-2">
          <CategoryChip
            label={`Tudo`}
            count={TEMPLATES.length}
            active={category === 'all'}
            onClick={() => setCategory('all')}
          />
          {CATEGORIES.map((c) => {
            const count = TEMPLATES.filter((t) => t.category === c).length;
            return (
              <CategoryChip
                key={c}
                label={CATEGORY_LABELS[c]}
                count={count}
                active={category === c}
                onClick={() => setCategory(c)}
              />
            );
          })}
        </div>

        {/* Grid */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {filtered.length === 0 && (
            <Card className="sm:col-span-2">
              <CardContent className="pt-6 text-muted-foreground text-sm">
                Nenhum template combina com "{search}".
              </CardContent>
            </Card>
          )}
          {filtered.map((t) => (
            <Card key={t.id}>
              <CardContent className="pt-6 flex flex-col h-full">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="font-serif text-lg">{t.title}</h3>
                  <Badge variant="muted" className="text-[10px] flex-none">
                    {CATEGORY_LABELS[t.category]}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground text-pretty flex-1">{t.description}</p>
                {t.tags && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {t.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center rounded-full bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between gap-3 pt-3 border-t border-border/40">
                  <span className="text-[11px] text-muted-foreground">Fonte: {t.source}</span>
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
                  >
                    <FileDown className="h-4 w-4" />
                    Acessar
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mt-10 bg-primary text-primary-foreground border-0">
          <CardContent className="pt-6 flex items-center justify-between gap-6 flex-wrap">
            <div>
              <h3 className="font-serif text-xl">Precisa de algo customizado?</h3>
              <p className="text-sm text-primary-foreground/80 mt-1">
                Advogados parceiros redigem do zero a partir de R$ 500.
              </p>
            </div>
            <Link
              to="/app/demanda/nova"
              className="rounded-md bg-accent text-accent-foreground px-4 py-2 text-sm font-medium hover:bg-accent/90"
            >
              Criar demanda
            </Link>
          </CardContent>
        </Card>
      </div>
    </MarketingLayout>
  );
}

function CategoryChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
        active
          ? 'border-accent bg-accent/15 text-foreground font-medium'
          : 'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground',
      )}
    >
      {label}
      <span className={cn('text-[10px]', active ? 'text-accent' : 'text-muted-foreground/60')}>
        {count}
      </span>
    </button>
  );
}
